import React, { useMemo } from 'react';
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';

/**
 * Centered modal rendering the Google-Docs-style review activity feed for a
 * shareable candidate review link. Each entry is one decision/note action made
 * by an anonymous (named) reviewer.
 */

const formatRelativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 45) return 'just now';
  if (diffSec < 90) return '1 minute ago';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minutes ago`;
  if (diffMin < 90) return '1 hour ago';
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hours ago`;
  if (diffHr < 36) return 'yesterday';
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} days ago`;
  return new Date(iso).toLocaleDateString();
};

const initialsFor = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const decisionLabel = (decision) => {
  switch (decision) {
    case 'selected':
      return 'Shortlisted';
    case 'rejected':
      return 'Rejected';
    case 'pending':
      return 'Marked as on-hold';
    case 'offered':
      return 'Offered';
    default:
      return 'Updated decision';
  }
};

const decisionPillClass = (decision) => {
  switch (decision) {
    case 'selected':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'rejected':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'pending':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'offered':
      return 'bg-violet-50 text-violet-700 border-violet-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const buildSentence = (entry) => {
  const candidate = entry.candidateName?.trim() || 'a candidate';
  // Render as "<verb-phrase> <candidate>" so the candidate name always lands at
  // the end. For decision actions where natural English needs the candidate in
  // the middle (e.g. "marked X as on-hold"), we return a fully-formed template.
  switch (entry.action) {
    case 'view':
      return { verb: 'viewed', target: 'the link' };
    case 'decision':
      if (entry.decision === 'selected') return { verb: 'shortlisted', target: candidate };
      if (entry.decision === 'rejected') return { verb: 'rejected', target: candidate };
      if (entry.decision === 'pending')
        return { verb: 'marked', target: `${candidate} as on-hold` };
      if (entry.decision === 'offered')
        return { verb: 'marked', target: `${candidate} as offered` };
      return { verb: 'updated decision for', target: candidate };
    case 'decision_cleared':
      return { verb: 'cleared the decision for', target: candidate };
    case 'note':
      return { verb: 'added a note on', target: candidate };
    case 'note_cleared':
      return { verb: 'removed the note on', target: candidate };
    default:
      return { verb: 'updated', target: candidate };
  }
};

const ReviewActivityDrawer = ({ isOpen, onClose, activities = [] }) => {
  const sorted = useMemo(
    () =>
      [...(activities || [])].sort(
        (a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime()
      ),
    [activities]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Review activity"
        className="relative w-full max-w-lg max-h-[85vh] bg-white rounded-xl shadow-2xl flex flex-col"
      >
        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">Review Activity</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {sorted.length === 0
                ? 'No activity yet on this share link.'
                : `${sorted.length} entr${sorted.length === 1 ? 'y' : 'ies'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
            aria-label="Close activity"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {sorted.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-12">
              <ClockIcon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
              When reviewers open the link, make decisions, or add notes, you&rsquo;ll see them here.
            </div>
          ) : (
            <ol className="space-y-4">
              {sorted.map((entry) => {
                const { verb, target } = buildSentence(entry);
                const showDecisionPill = entry.action === 'decision' && entry.decision;
                const isRecruiter = entry.actorType === 'recruiter';
                return (
                  <li key={entry.id} className="flex gap-3">
                    <div className={`flex-shrink-0 h-8 w-8 rounded-full text-xs font-semibold flex items-center justify-center ${isRecruiter ? 'bg-violet-100 text-violet-700' : 'bg-[#007BFF]/10 text-[#007BFF]'}`}>
                      {initialsFor(entry.reviewerName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-900 flex flex-wrap items-center gap-x-1 gap-y-0.5">
                        <span className="font-semibold">{entry.reviewerName || 'Anonymous'}</span>{' '}
                        {isRecruiter && (
                          <span className="mr-1 inline-flex items-center rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 border border-violet-200">
                            Recruiter
                          </span>
                        )}
                        <span className="text-gray-700 whitespace-nowrap">{verb}</span>{' '}
                        <span className="font-medium text-gray-900 break-words">{target}</span>
                        {showDecisionPill && (
                          <span
                            className={`ml-2 inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${decisionPillClass(
                              entry.decision
                            )}`}
                          >
                            {decisionLabel(entry.decision)}
                          </span>
                        )}
                      </p>
                      {entry.note && (
                        <blockquote className="mt-1.5 text-xs text-gray-700 bg-gray-50 border-l-2 border-gray-300 px-3 py-2 rounded-r whitespace-pre-line break-words max-h-24 overflow-y-auto">
                          {entry.note}
                        </blockquote>
                      )}
                      <p className="mt-1.5 text-[11px] text-gray-400">
                        {formatRelativeTime(entry.at)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewActivityDrawer;
